import { Injectable } from '@angular/core';
import { PermissionService } from './permission.service';
import { PAGE_ACCESS, PageAccess } from './page-access';
import { CUSTOMER_NAV_TABS, INavTabItem } from 'app/components/global/nav/nav-elements-data';

/** Shown when the user's role opens no page of the customer navigation */
export const NO_ACCESS_PATH = '/no-access';

/**
 * Which customer pages the user can open, for the navigation and for "go home" redirects.
 *
 * The rule: a page opens when the user has the level its route asks for (PermissionGuard) and the
 * customer has its feature (FeatureGuard). Both come from PAGE_ACCESS, which is also the route data,
 * so the navigation shows exactly the pages that open (a menu item never bounces) and the start page
 * is the first of them in menu order.
 */
@Injectable({ providedIn: 'root' })
export class StartPageService {
  constructor(private permissions: PermissionService) { }

  /** Can the user open the page at this path ("/reports/statistics") */
  canOpen(path: string, features: string[] = readFeatures()): boolean {
    const page: PageAccess | undefined = PAGE_ACCESS[path.replace(/^\//, '')];
    if (!page) return false;
    if (page.feature && !features.includes(page.feature)) return false;
    return !page.area || this.permissions.has(page.area, page.access ?? 'Read');
  }

  /** Is the navigation item on: its feature conditions hold and its page opens */
  isNavItemOpen(tab: INavTabItem, features: string[] = readFeatures()): boolean {
    return conditionHolds(tab.userCondition, features) && conditionHolds(tab.deviceCondition, features) &&
      this.canOpen(tab.path, features);
  }

  /** The first page of the customer navigation the user can open, or the no-access page */
  startPage(): string {
    const features = readFeatures();
    return CUSTOMER_NAV_TABS.find(tab => this.isNavItemOpen(tab, features))?.path ?? NO_ACCESS_PATH;
  }
}

/** A navigation condition: true, or the name of a feature the customer must have */
function conditionHolds(condition: boolean | string | undefined, features: string[]): boolean {
  if (condition === undefined || condition === true) return true;
  return typeof condition === 'string' && features.includes(condition);
}

function readFeatures(): string[] {
  try {
    return JSON.parse(localStorage.getItem('features')) ?? [];
  } catch {
    return [];
  }
}
